'use client';
import { useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { useTranslation } from '@/i18n/client';
// -------------- api -------------------- //
import { fetchBestSellerData } from '@/apis/apiBestSeller';
// -------------- 组件 -------------------- //
import JDIProductCard from '@/components/productCard/index';
import {
  getRankIcon,
  goToCurrentRankPage,
  returnCurrentRankPage,
} from '@/app/promo/best-seller/utils';
import UKPageNothing from '@/components/UKPageNothing';
// -------------- 图标 -------------------- //
import ArrowRight from '@/assets/icons/ArrowRightBlack.svg';
// -------------- utils -------------------- //
import { EXPCONFIG_FAST } from '@/constants/point';
import cookieUtils from '@/intl-jdi-common-ssr/utils/cookieUtils';
// -------------- styles -------------------- //
import styles from '@/app/promo/best-seller/page.module.scss';
// -------------- pv -------------------- //
import { sendPv, sendClickTrack, EXPOSURE_RECORD } from '@/tracks/25329';
import { ProductType } from '@/components/productCard/index.interface';
// -------------- styles -------------------- //
// -------------- pv -------------------- //
import BestSellerMainCard from './components/BestSellerMainCard';
import BestSellerSidebar from './components/BestSellerSidebar';
import { useSearchParams } from 'next/navigation';
import { useInViewport } from 'ahooks';
import { debounce } from 'lodash';

export interface IBestSellerClientProps {
  bestSellerCategoryData: any;
  sidebarData: any[];
  localInfo: any;
  total: number;
}

export default function BestSellerClient({
  bestSellerCategoryData,
  sidebarData,
  localInfo,
  total,
}: IBestSellerClientProps) {
  const searchParams = useSearchParams();
  const { t } = useTranslation();
  const [datas, setDatas] = useState(bestSellerCategoryData?.data);
  // is client
  const [isClient, setIsClient] = useState<boolean>(false);
  // totalPage
  const [totalPage, setTotalPage] = useState<number>(total);
  // current Page
  const [currentPage, setCurrentPage] = useState<number>(bestSellerCategoryData?.currentPage);
  // loading
  const [loading, setLoading] = useState<boolean>(false);
  // Last page
  const [lastPage, setLastPage] = useState<boolean>(datas.length >= total);
  // Track user selection
  const tagIndex = sidebarData.findIndex(
    (item: any) => item.sequenceId === searchParams.get('tag'),
  );
  const [selected, setSelected] = useState<number>(tagIndex !== -1 ? tagIndex : 0);
  const sentinelRef = useRef<HTMLElement>(null);
  // first Screen
  const firstLoadRef = useRef(true);
  const [isVisible, ratio] = useInViewport(sentinelRef.current);
  const [sequenceId, setSequenceId] = useState<string | null>(null);

  useEffect(() => {
    sendPv.Activity_BestSellersHome();

    setIsClient(true);
  }, []);

  useEffect(() => {
    if (isVisible) {
      // 首次进入，直接跳过
      if (firstLoadRef.current) {
        firstLoadRef.current = false;
        return;
      }
      if (currentPage < totalPage) {
        debouncedHandleScroll();
      }
    }
  }, [isVisible]);

  // // -------------- scroll handler -------------------- //
  const handleScrollImmediate = useCallback(async () => {
    if (currentPage >= totalPage || lastPage) {
      return;
    }

    setLoading(true);

    try {
      const nextPage = currentPage + 1;
      const result = await fetchBestSellerData({ currentPage: nextPage, sequenceId: sequenceId });

      if (result.success && result.data.data.data) {
        setDatas((prev: any) => [...prev, ...result.data.data.data]);
        setCurrentPage(nextPage);
        setLastPage(datas.length >= result.data.data.total);
        setTotalPage(result.data.data.total);
      }
    } catch (error) {
      console.error('加载更多数据失败：', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, totalPage]);

  const debouncedHandleScroll = useMemo(
    () => debounce(handleScrollImmediate, 200),
    [handleScrollImmediate],
  );

  // fetch new data according to sequenceID
  async function handleFetch(sequenceId: string) {
    firstLoadRef.current = true;
    setLoading(true);
    setDatas([]);
    setSequenceId(sequenceId);
    const res = await fetchBestSellerData({ currentPage: 1, sequenceId: sequenceId });
    const newData = res?.data?.data?.data || [];
    setDatas(newData);
    setTotalPage(res?.data?.data?.total);
    setLastPage(newData?.length >= res?.data?.data?.total);
    setCurrentPage(1);
    setLoading(false);
  }

  return (
    <div className={styles.innerContainer} id='best-seller-container'>
      <div className={styles.sidebar}>
        <BestSellerSidebar
          sidebarData={sidebarData}
          selected={selected}
          setSelected={setSelected}
          handleFetch={handleFetch}
        />
      </div>

      <div className={styles.mainContentContainer}>
        {datas &&
          datas?.map((item: any, index: number) => {
            return (
              <BestSellerMainCard
                key={item.sequenceId}
                sequenceId={item.sequenceId}
                data={item.productBestSellerRanks}
                currencyCode={localInfo.currencyCode}
                languageCode={localInfo.languageCode}
                item={item}
                index={index}
                datas={datas}
              />
            );
          })}
        <div ref={sentinelRef as any} style={{ height: '1px' }}></div>
        <div className={styles.end}>
          {loading ? t('bestSeller.loading') : lastPage && t('bestSeller.recommend.endTxt')}
        </div>
      </div>
    </div>
  );
}
